import { User } from 'lucide-react';
import { DailyUpdateCard } from './DailyUpdateCard';
import type { DailyUpdateDayGroup } from '../../features/dailyUpdates/dailyUpdateModel';
import type { DailyUpdateSummary } from '../../features/dailyUpdates/types';

export interface DailyUpdateFeedProps {
  dayGroups: DailyUpdateDayGroup[];
  storyTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  goalTitles: Map<string, string>;
  onOpenWorkstream: (workstreamId: string) => void;
  onOpenGoal: (goalId: string) => void;
  onConvertBlocker: (update: DailyUpdateSummary) => void;
  onDelete: (update: DailyUpdateSummary) => void;
}

/**
 * The daily-updates feed, grouped by day (most recent first) and, within a day,
 * by engineer. This is the TEAM-CONSUME surface: a scannable "what did everyone
 * do / what's next" digest, never a per-person scoreboard.
 */
export function DailyUpdateFeed({
  dayGroups,
  storyTitles,
  workstreamTitles,
  goalTitles,
  onOpenWorkstream,
  onOpenGoal,
  onConvertBlocker,
  onDelete,
}: DailyUpdateFeedProps) {
  return (
    <div className="space-y-8">
      {dayGroups.map((day) => (
        <section key={day.date}>
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-zinc-100">{day.label}</h2>
            <span className="text-xs text-zinc-500">
              {day.count} {day.count === 1 ? 'update' : 'updates'}
            </span>
          </div>

          <div className="space-y-5">
            {day.engineers.map((eng) => (
              <div key={`${day.date}:${eng.engineer}`}>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-zinc-400">
                  <User className="h-3.5 w-3.5" aria-hidden="true" />
                  {eng.engineer}
                </div>
                <div className="space-y-3">
                  {eng.updates.map((u) => (
                    <DailyUpdateCard
                      key={u.id}
                      update={u}
                      storyTitle={u.storyId ? storyTitles.get(u.storyId) : undefined}
                      workstreamTitle={
                        u.workstreamId ? workstreamTitles.get(u.workstreamId) : undefined
                      }
                      goalTitle={u.goalId ? goalTitles.get(u.goalId) : undefined}
                      onOpenWorkstream={onOpenWorkstream}
                      onOpenGoal={onOpenGoal}
                      onConvertBlocker={onConvertBlocker}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default DailyUpdateFeed;
